import React, { useContext, useState, useEffect } from 'react';
import axios from 'axios';
import {
    Box,
    Container,
    Heading,
    Text,
    VStack,
    HStack,
    useToast,
    Card,
    CardBody,
    Flex,
    SimpleGrid,
    Spinner,
    Button,
    Stat,
    StatLabel,
    StatNumber,
    StatHelpText,
    useBreakpointValue,
    Badge,
    Icon,
    Divider,
    Progress,
} from '@chakra-ui/react';
import {
    FaSync,
    FaShieldAlt,
    FaExclamationTriangle,
    FaCheckCircle,
    FaChartPie,
    FaBrain,
    FaFileExport,
    FaTachometerAlt,
    FaLock,
    FaExclamationCircle,
    FaChartBar,
    FaBalanceScale,
    FaFileAlt,
    FaDownload
} from 'react-icons/fa';
import {
    BarChart,
    Bar,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip as RechartsTooltip,
    ResponsiveContainer,
    Cell,
    PieChart,
    Pie,
} from 'recharts';
import AuthContext from '../../context/AuthContext';
import ThemeContext from '../../context/ThemeContext';
import EmptyStateCard from '../../compnents/EmptyStateCard';
import DashboardRoleBrief from '../../compnents/DashboardRoleBrief';
import RoleOperationalKpis from '../../compnents/RoleOperationalKpis';
import ProductDataSignalsPanel from '../../compnents/ProductDataSignalsPanel';
import DataReadinessStrip from '../../compnents/DataReadinessStrip';
import SyntheticPreviewBanner from '../../compnents/SyntheticPreviewBanner';
import { useNavigate } from 'react-router-dom';
import { demoComplianceDashboard } from '../../content/demoAnalytics';

const isProduction = process.env.NODE_ENV === 'production';
const API_BASE_URL = isProduction
    ? process.env.REACT_APP_API_BASE_URL_PROD
    : process.env.REACT_APP_API_BASE_URL_DEV;

function DashboardComplianceDashboard() {
    const { token } = useContext(AuthContext);
    const { themeData } = useContext(ThemeContext);
    const navigate = useNavigate();
    const [loading, setLoading] = useState(false);
    const [dashboardData, setDashboardData] = useState(null);
    const [downloadingPng, setDownloadingPng] = useState(false); // Add this line
    const [lastRefreshedAt, setLastRefreshedAt] = useState(null);
    const toast = useToast();

    const statSize = useBreakpointValue({ base: 'sm', md: 'md' });

    // Extract theme colors with fallbacks
    const primaryColor = themeData?.colors?.primary || '#0B2545';
    const secondaryColor = themeData?.colors?.secondary || '#13315C';
    const accentColor = themeData?.colors?.accent || '#13C1BB';
    const backgroundColor = themeData?.colors?.background || '#F5F7FA';

    // Determine if theme is dark (midnight)
    const isDarkTheme = themeData?.id === 'midnight';

    // Card background - improved for dark theme
    const cardBg = isDarkTheme ? 'rgba(45, 55, 72, 0.6)' : 'white';

    // Text colors with proper contrast
    const headingTextColor = isDarkTheme ? '#F5F7FA' : primaryColor;
    const bodyTextColor = isDarkTheme ? '#E2E8F0' : 'gray.600';
    const mutedTextColor = isDarkTheme ? '#CBD5E0' : 'gray.500';
    const labelTextColor = isDarkTheme ? '#E2E8F0' : 'gray.700';

    // Border colors
    const borderColor = isDarkTheme ? 'rgba(66, 153, 225, 0.3)' : 'gray.200';
    const chartGridColor = isDarkTheme ? 'rgba(66, 153, 225, 0.25)' : '#E2E8F0';

    // Status colors - lighter for dark theme
    const successColor = isDarkTheme ? '#68D391' : '#38A169';
    const errorColor = isDarkTheme ? '#FC8181' : '#E53E3E';
    const warningColor = isDarkTheme ? '#F6AD55' : '#DD6B20';
    const infoColor = isDarkTheme ? '#13C1BB' : '#3182CE';
    const purpleColor = isDarkTheme ? '#B794F4' : '#805AD5';

    // Info card backgrounds
    const successBg = isDarkTheme ? 'rgba(104, 211, 145, 0.15)' : '#D1FAE5';
    const errorBg = isDarkTheme ? 'rgba(252, 129, 129, 0.15)' : '#FEE2E2';
    const warningBg = isDarkTheme ? 'rgba(246, 173, 85, 0.15)' : '#FED7AA';
    const infoBg = isDarkTheme ? 'rgba(66, 153, 225, 0.15)' : accentColor + '15';

    // Button hover
    const buttonHoverBg = isDarkTheme ? accentColor : secondaryColor;

    // Chart axis color
    const axisColor = isDarkTheme ? bodyTextColor : '#4A5568';

    // Color palettes
    const RISK_COLORS = isDarkTheme
        ? [successColor, warningColor, errorColor]
        : [successColor, warningColor, errorColor];

    const SEVERITY_COLORS = isDarkTheme
        ? [successColor, '#68D391', warningColor, errorColor]
        : ['#22C55E', successColor, warningColor, errorColor];

    const fetchDashboardData = async () => {
        try {
            setLoading(true);
            const res = await axios.get(`${API_BASE_URL}/content/compliance-dashboard`, {
                headers: { Authorization: `Bearer ${token}` },
            });
            setDashboardData(res.data?.metrics ? res.data : demoComplianceDashboard);
            setLastRefreshedAt(new Date());
        } catch (error) {
            console.error('Dashboard fetch error:', error);
            setDashboardData(demoComplianceDashboard);
            setLastRefreshedAt(new Date());
        } finally {
            setLoading(false);
        }
    };

    const handleExportPng = async () => {
        try {
            setDownloadingPng(true);
            const response = await axios.get(`${API_BASE_URL}/content/export-png/compliance`, {
                headers: { Authorization: `Bearer ${token}` },
                responseType: 'blob', // Important for file download
            });

            // Create a download link and trigger it
            const blob = new Blob([response.data], { type: 'image/png' });
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.download = `dashboard_compliance_${Date.now()}.png`;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(url);

            toast({
                title: 'Success',
                description: 'Dashboard PNG snapshot exported. PDF and CSV packs are available in Reports.',
                status: 'success',
                duration: 3000,
                isClosable: true,
                position: 'top',
            });
        } catch (error) {
            console.error('Export PNG error:', error);
            toast({
                title: 'Error',
                description: error.response?.data?.message || 'Failed to export dashboard.',
                status: 'error',
                duration: 5000,
                isClosable: true,
                position: 'top',
            });
        } finally {
            setDownloadingPng(false);
        }
    };

    useEffect(() => {
        fetchDashboardData();
        // eslint-disable-next-line
    }, []);

    // Prepare risk distribution data
    const getRiskDistributionData = () => {
        if (!dashboardData?.risk_distribution) return [];
        const dist = dashboardData.risk_distribution;
        return [
            { name: 'Low Risk', value: dist.low, color: RISK_COLORS[0] },
            { name: 'Medium Risk', value: dist.medium, color: RISK_COLORS[1] },
            { name: 'High Risk', value: dist.high, color: RISK_COLORS[2] },
        ];
    };

    // Prepare bias severity data
    const getBiasSeverityData = () => {
        if (!dashboardData?.bias_severity_summary?.severity_distribution) return [];
        const dist = dashboardData.bias_severity_summary.severity_distribution;
        return [
            { name: 'Low', value: dist.low, color: SEVERITY_COLORS[0] },
            { name: 'Medium', value: dist.medium, color: SEVERITY_COLORS[1] },
            { name: 'High', value: dist.high, color: SEVERITY_COLORS[2] },
            { name: 'Critical', value: dist.critical, color: SEVERITY_COLORS[3] },
        ];
    };

    const metrics = dashboardData?.metrics || {};
    const riskDist = getRiskDistributionData();
    const biasSeverity = getBiasSeverityData();

    // Compliance score is derived from evidence coverage and high-risk share,
    // both computed from tenant data. Until the evidence dataset is loaded
    // there is no defensible score, so the card is hidden instead of invented.
    const evidenceCoveragePct = Number(metrics.audit_evidence_completeness_pct);
    const hasEvidenceCoverage = Number.isFinite(evidenceCoveragePct);
    const evidenceReview = metrics.evidence_review || null;
    const openReviewItems = evidenceReview
        ? Number(evidenceReview.pending_evidence_items || 0)
            + Number(evidenceReview.advised_trades_without_evidence || 0)
            + Number(evidenceReview.risk_mismatch_cases || 0)
        : null;

    // No composite "compliance score": coverage and high-risk share are shown
    // as measured. Whether a firm is compliant is not something this data can
    // establish.
    const evidenceCoverageShown = dashboardData && hasEvidenceCoverage ? evidenceCoveragePct.toFixed(1) : null;
    const lastUpdatedLabel = lastRefreshedAt ? lastRefreshedAt.toLocaleString() : null;
    const criticalBiasCount = biasSeverity.find((item) => item.name === 'Critical')?.value || 0;
    const briefingHighlights = [
        {
            label: 'High-risk share',
            value: `${(metrics.regulatory_compliance_monitoring?.high_risk_percentage || 0).toFixed(1)}%`,
            help: 'Customers currently in the higher-risk monitoring bucket',
        },
        {
            label: 'Evidence coverage',
            value: hasEvidenceCoverage ? `${Math.round(evidenceCoveragePct)}%` : 'n/a',
            help: hasEvidenceCoverage
                ? 'Customers with linked suitability evidence'
                : 'Upload the suitability evidence dataset to compute coverage',
        },
        {
            label: 'Critical bias cases',
            value: criticalBiasCount,
            help: 'Cases that deserve first review before export or escalation',
        },
    ];
    const briefingFocusAreas = [
        `Review ${criticalBiasCount} critical-severity cases before lower-severity exceptions`,
        'Cross-check benchmark and score tabs before exporting reviewer evidence',
        'Refresh after a new dataset or controls update to keep audit material current',
    ];

    return (
        <Box minH="100vh" width="100%" pb={{ base: '90px', md: '20px' }} bg={backgroundColor}>
            <Container maxW="container.xl" py={{ base: 4, md: 8 }} px={{ base: 3, md: 6 }}>
                <VStack spacing={{ base: 4, md: 6 }} align="stretch">
                    <DataReadinessStrip role="Compliance" />
                    {dashboardData?.isSyntheticDemo ? (
                        <SyntheticPreviewBanner message="Live tenant metrics are not available yet, so this dashboard is showing a synthetic demo sample. Compliance values will switch to firm data once scoring metrics are returned for this tenant." />
                    ) : null}
                    {/* Header */}
                    <Flex justify="space-between" align="center" mb={4} flexWrap="wrap" gap={4}>
                        <Box flex="1">
                            <Heading size="lg" color={headingTextColor} mb={2}>
                                Compliance - Customer Bias Analysis
                            </Heading>
                            <Text color={bodyTextColor} fontSize="sm">
                                Compliance view: Monitor and review behavioral biases across all customers
                            </Text>
                        </Box>
                        <Button
                            leftIcon={<FaDownload />}
                            bg={accentColor}
                            color="white"
                            size={{ base: 'sm', md: 'md' }}
                            onClick={handleExportPng}
                            isLoading={downloadingPng}
                            _hover={{ bg: isDarkTheme ? secondaryColor : accentColor }}
                        >
                            Export PNG snapshot
                        </Button>
                        <Button
                            leftIcon={<FaFileExport />}
                            variant="outline"
                            colorScheme="blue"
                            size={{ base: 'sm', md: 'md' }}
                            onClick={() => navigate('/reportsExports')}
                        >
                            PDF / CSV reports
                        </Button>
                        <Button
                            leftIcon={<FaSync />}
                            bg={primaryColor}
                            color="white"
                            size={{ base: 'sm', md: 'md' }}
                            onClick={fetchDashboardData}
                            isLoading={loading}
                            _hover={{ bg: buttonHoverBg }}
                        >
                            Refresh
                        </Button>
                    </Flex>

                    {loading && (
                        <Flex justify="center" align="center" py={20}>
                            <VStack spacing={4}>
                                <Spinner size="xl" color={primaryColor} thickness="4px" />
                                <Text color={mutedTextColor}>Loading compliance data...</Text>
                            </VStack>
                        </Flex>
                    )}

                    {!loading && dashboardData && (
                        <>
                            <DashboardRoleBrief
                                title="Keep compliance review evidence-ready"
                                description="This dashboard works best when it quickly surfaces exception pressure, severity pockets, and whether the underlying evidence is ready for supervisory review."
                                highlights={briefingHighlights}
                                focusAreas={briefingFocusAreas}
                                lastUpdatedLabel={lastUpdatedLabel}
                                bg={cardBg}
                                borderColor={borderColor}
                                titleColor={headingTextColor}
                                descriptionColor={bodyTextColor}
                                accentColor={isDarkTheme ? accentColor : primaryColor}
                                cardBg={isDarkTheme ? 'rgba(45, 55, 72, 0.45)' : 'gray.50'}
                            />

                            <RoleOperationalKpis
                                role="compliance"
                                bg={cardBg}
                                borderColor={borderColor}
                                titleColor={headingTextColor}
                                descriptionColor={bodyTextColor}
                                accentColor={isDarkTheme ? accentColor : primaryColor}
                                cardBg={isDarkTheme ? 'rgba(45, 55, 72, 0.45)' : 'gray.50'}
                            />

                            {/* Suitability evidence coverage — only when the evidence dataset is loaded */}
                            {evidenceCoverageShown !== null && (
                                <Card bg={cardBg} borderRadius="xl" boxShadow="lg" borderWidth="1px">
                                    <CardBody p={{ base: 4, md: 6 }}>
                                        <VStack spacing={3}>
                                            <Heading size={{ base: 'md', md: 'lg' }} color={headingTextColor}>
                                                Suitability evidence coverage
                                            </Heading>
                                            <Text fontSize={{ base: '4xl', md: '6xl' }} fontWeight="bold" color={headingTextColor}>
                                                {evidenceCoverageShown}%
                                            </Text>
                                            <Progress value={Number(evidenceCoverageShown)} w="100%" h="8px" borderRadius="full" colorScheme="teal" />
                                            <Text color={bodyTextColor} fontSize="sm" textAlign="center">
                                                Share of clients with linked suitability evidence in the uploaded dataset. It describes the data you provided; it is not a compliance rating.
                                            </Text>
                                        </VStack>
                                    </CardBody>
                                </Card>
                            )}

                            {/* Key Metrics Grid */}
                            <SimpleGrid columns={{ base: 2, md: 4 }} spacing={{ base: 3, md: 4 }}>
                                <Card
                                    bg={cardBg}
                                    borderRadius="xl"
                                    boxShadow="md"
                                    border="1px"
                                    borderColor={borderColor}
                                    transition="all 0.2s"
                                    _hover={{ transform: 'translateY(-2px)', boxShadow: 'xl' }}
                                >
                                    <CardBody p={{ base: 4, md: 5 }}>
                                        <Stat size={statSize}>
                                            <HStack spacing={2} mb={1}>
                                                <Icon as={FaChartPie} color={isDarkTheme ? accentColor : primaryColor} boxSize={{ base: 4, md: 5 }} />
                                                <StatLabel fontSize={{ base: 'xs', md: 'sm' }} color={bodyTextColor}>
                                                    Total Customers
                                                </StatLabel>
                                            </HStack>
                                            <StatNumber fontSize={{ base: '2xl', md: '3xl' }} color={isDarkTheme ? accentColor : primaryColor} fontWeight="bold">
                                                {dashboardData.total_customers?.toLocaleString()}
                                            </StatNumber>
                                            <StatHelpText color={mutedTextColor} fontSize={{ base: 'xs', md: 'sm' }}>
                                                Under monitoring
                                            </StatHelpText>
                                        </Stat>
                                    </CardBody>
                                </Card>

                                <Card
                                    bg={cardBg}
                                    borderRadius="xl"
                                    boxShadow="md"
                                    border="1px"
                                    borderColor={borderColor}
                                    transition="all 0.2s"
                                    _hover={{ transform: 'translateY(-2px)', boxShadow: 'xl' }}
                                >
                                    <CardBody p={{ base: 4, md: 5 }}>
                                        <Stat size={statSize}>
                                            <HStack spacing={2} mb={1}>
                                                <Icon as={FaExclamationCircle} color={errorColor} boxSize={{ base: 4, md: 5 }} />
                                                <StatLabel fontSize={{ base: 'xs', md: 'sm' }} color={bodyTextColor}>
                                                    High Risk Clients
                                                </StatLabel>
                                            </HStack>
                                            <StatNumber fontSize={{ base: '2xl', md: '3xl' }} color={errorColor} fontWeight="bold">
                                                {metrics.regulatory_compliance_monitoring?.high_risk_clients_count ?? 0}
                                            </StatNumber>
                                            <StatHelpText color={mutedTextColor} fontSize={{ base: 'xs', md: 'sm' }}>
                                                {(metrics.regulatory_compliance_monitoring?.high_risk_percentage || 0).toFixed(1)}% of book
                                            </StatHelpText>
                                        </Stat>
                                    </CardBody>
                                </Card>

                                <Card
                                    bg={cardBg}
                                    borderRadius="xl"
                                    boxShadow="md"
                                    border="1px"
                                    borderColor={borderColor}
                                    transition="all 0.2s"
                                    _hover={{ transform: 'translateY(-2px)', boxShadow: 'xl' }}
                                >
                                    <CardBody p={{ base: 4, md: 5 }}>
                                        <Stat size={statSize}>
                                            <HStack spacing={2} mb={1}>
                                                <Icon as={FaCheckCircle} color={hasEvidenceCoverage ? successColor : mutedTextColor} boxSize={{ base: 4, md: 5 }} />
                                                <StatLabel fontSize={{ base: 'xs', md: 'sm' }} color={bodyTextColor}>
                                                    Evidence Coverage
                                                </StatLabel>
                                            </HStack>
                                            <StatNumber fontSize={{ base: '2xl', md: '3xl' }} color={hasEvidenceCoverage ? successColor : mutedTextColor} fontWeight="bold">
                                                {hasEvidenceCoverage ? `${Math.round(evidenceCoveragePct)}%` : '—'}
                                            </StatNumber>
                                            <StatHelpText color={mutedTextColor} fontSize={{ base: 'xs', md: 'sm' }}>
                                                {hasEvidenceCoverage ? 'Customers with suitability evidence' : 'Needs evidence dataset'}
                                            </StatHelpText>
                                        </Stat>
                                    </CardBody>
                                </Card>

                                <Card
                                    bg={cardBg}
                                    borderRadius="xl"
                                    boxShadow="md"
                                    border="1px"
                                    borderColor={borderColor}
                                    transition="all 0.2s"
                                    _hover={{ transform: 'translateY(-2px)', boxShadow: 'xl' }}
                                >
                                    <CardBody p={{ base: 4, md: 5 }}>
                                        <Stat size={statSize}>
                                            <HStack spacing={2} mb={1}>
                                                <Icon as={FaShieldAlt} color={openReviewItems > 0 ? warningColor : successColor} boxSize={{ base: 4, md: 5 }} />
                                                <StatLabel fontSize={{ base: 'xs', md: 'sm' }} color={bodyTextColor}>
                                                    Open Review Items
                                                </StatLabel>
                                            </HStack>
                                            <StatNumber fontSize={{ base: '2xl', md: '3xl' }} color={openReviewItems > 0 ? warningColor : successColor} fontWeight="bold">
                                                {openReviewItems === null ? '—' : openReviewItems}
                                            </StatNumber>
                                            <StatHelpText color={mutedTextColor} fontSize={{ base: 'xs', md: 'sm' }}>
                                                {openReviewItems === null ? 'Needs firm datasets' : 'Evidence, trades, mismatches'}
                                            </StatHelpText>
                                        </Stat>
                                    </CardBody>
                                </Card>
                            </SimpleGrid>

                            {/* Risk Management Metrics — leverage/drawdown only exist in the
                                synthetic demo payload; the ingest does not capture them yet. */}
                            {metrics.leverage_ratios && (
                            <Card
                                bg={cardBg}
                                borderRadius="lg"
                                boxShadow="md"
                                borderWidth="1px"
                                borderColor={borderColor}
                            >
                                <CardBody>
                                    <HStack spacing={2} mb={4}>
                                        <Icon as={FaBalanceScale} color={isDarkTheme ? accentColor : primaryColor} boxSize={6} />
                                        <Heading size={{ base: 'sm', md: 'md' }} color={headingTextColor}>
                                            Risk Management Metrics
                                        </Heading>
                                    </HStack>
                                    <SimpleGrid columns={{ base: 1, md: 4 }} spacing={4}>
                                        <Card bg={infoBg} borderRadius="md" boxShadow="sm">
                                            <CardBody>
                                                <Stat size="sm">
                                                    <StatLabel color={bodyTextColor}>Mean Leverage</StatLabel>
                                                    <StatNumber color={labelTextColor}>{metrics.leverage_ratios?.mean?.toFixed(2)}x</StatNumber>
                                                    <StatHelpText color={mutedTextColor}>Average ratio</StatHelpText>
                                                </Stat>
                                            </CardBody>
                                        </Card>

                                        <Card bg={warningBg} borderRadius="md" boxShadow="sm">
                                            <CardBody>
                                                <Stat size="sm">
                                                    <StatLabel color={bodyTextColor}>Max Leverage</StatLabel>
                                                    <StatNumber color={warningColor}>{metrics.leverage_ratios?.max?.toFixed(2)}x</StatNumber>
                                                    <StatHelpText color={mutedTextColor}>Highest observed</StatHelpText>
                                                </Stat>
                                            </CardBody>
                                        </Card>

                                        <Card bg={errorBg} borderRadius="md" boxShadow="sm">
                                            <CardBody>
                                                <Stat size="sm">
                                                    <StatLabel color={bodyTextColor}>High Leverage</StatLabel>
                                                    <StatNumber color={errorColor}>{metrics.leverage_ratios?.clients_above_2x}</StatNumber>
                                                    <StatHelpText color={mutedTextColor}>Clients above 2x</StatHelpText>
                                                </Stat>
                                            </CardBody>
                                        </Card>

                                        <Card bg={warningBg} borderRadius="md" boxShadow="sm">
                                            <CardBody>
                                                <Stat size="sm">
                                                    <StatLabel color={bodyTextColor}>Avg Drawdown</StatLabel>
                                                    <StatNumber color={warningColor}>{metrics.average_max_drawdown?.toFixed(2)}%</StatNumber>
                                                    <StatHelpText color={mutedTextColor}>Portfolio average</StatHelpText>
                                                </Stat>
                                            </CardBody>
                                        </Card>
                                    </SimpleGrid>
                                </CardBody>
                            </Card>
                            )}

                            {/* Regulatory Compliance Alert */}
                            <Card
                                bg={metrics.regulatory_compliance_monitoring?.high_risk_clients_count > 0 ? errorBg : successBg}
                                borderRadius="lg"
                                boxShadow="md"
                                borderWidth="2px"
                                borderColor={metrics.regulatory_compliance_monitoring?.high_risk_clients_count > 0 ? errorColor : successColor}
                            >
                                <CardBody>
                                    <HStack spacing={2} mb={4}>
                                        <Icon
                                            as={metrics.regulatory_compliance_monitoring?.high_risk_clients_count > 0 ? FaExclamationTriangle : FaCheckCircle}
                                            color={metrics.regulatory_compliance_monitoring?.high_risk_clients_count > 0 ? errorColor : successColor}
                                            boxSize={6}
                                        />
                                        <Heading size={{ base: 'sm', md: 'md' }} color={headingTextColor}>
                                            Regulatory Compliance Monitoring
                                        </Heading>
                                    </HStack>
                                    <SimpleGrid columns={{ base: 2, md: 4 }} spacing={4}>
                                        <VStack align="start" spacing={1}>
                                            <Text fontSize="xs" color={bodyTextColor}>High Risk Clients</Text>
                                            <Text fontSize="2xl" fontWeight="bold" color={labelTextColor}>
                                                {metrics.regulatory_compliance_monitoring?.high_risk_clients_count}
                                            </Text>
                                            <Badge colorScheme={metrics.regulatory_compliance_monitoring?.high_risk_clients_count > 0 ? 'red' : 'green'}>
                                                {metrics.regulatory_compliance_monitoring?.high_risk_percentage?.toFixed(1)}%
                                            </Badge>
                                        </VStack>

                                        {metrics.regulatory_compliance_monitoring?.clients_with_excessive_leverage !== undefined && (
                                            <VStack align="start" spacing={1}>
                                                <Text fontSize="xs" color={bodyTextColor}>Excessive Leverage</Text>
                                                <Text fontSize="2xl" fontWeight="bold" color={labelTextColor}>
                                                    {metrics.regulatory_compliance_monitoring?.clients_with_excessive_leverage}
                                                </Text>
                                                <Badge colorScheme={metrics.regulatory_compliance_monitoring?.clients_with_excessive_leverage > 0 ? 'red' : 'green'}>
                                                    Clients
                                                </Badge>
                                            </VStack>
                                        )}

                                        {metrics.regulatory_compliance_monitoring?.clients_with_severe_drawdowns !== undefined && (
                                            <VStack align="start" spacing={1}>
                                                <Text fontSize="xs" color={bodyTextColor}>Severe Drawdowns</Text>
                                                <Text fontSize="2xl" fontWeight="bold" color={labelTextColor}>
                                                    {metrics.regulatory_compliance_monitoring?.clients_with_severe_drawdowns}
                                                </Text>
                                                <Badge colorScheme={metrics.regulatory_compliance_monitoring?.clients_with_severe_drawdowns > 0 ? 'red' : 'green'}>
                                                    Clients
                                                </Badge>
                                            </VStack>
                                        )}

                                        {metrics.days_since_last_breach !== undefined && (
                                            <VStack align="start" spacing={1}>
                                                <Text fontSize="xs" color={bodyTextColor}>Days Since Breach</Text>
                                                <Text fontSize="2xl" fontWeight="bold" color={labelTextColor}>
                                                    {metrics.days_since_last_breach ?? 'N/A'}
                                                </Text>
                                                <Badge colorScheme="blue">
                                                    Last incident
                                                </Badge>
                                            </VStack>
                                        )}

                                        {evidenceReview && (
                                            <VStack align="start" spacing={1}>
                                                <Text fontSize="xs" color={bodyTextColor}>Pending Evidence</Text>
                                                <Text fontSize="2xl" fontWeight="bold" color={labelTextColor}>
                                                    {evidenceReview.pending_evidence_items}
                                                </Text>
                                                <Badge colorScheme={evidenceReview.pending_evidence_items > 0 ? 'orange' : 'green'}>
                                                    Items to review
                                                </Badge>
                                            </VStack>
                                        )}

                                        {evidenceReview && (
                                            <VStack align="start" spacing={1}>
                                                <Text fontSize="xs" color={bodyTextColor}>Unlinked Advised Trades</Text>
                                                <Text fontSize="2xl" fontWeight="bold" color={labelTextColor}>
                                                    {evidenceReview.advised_trades_without_evidence}
                                                </Text>
                                                <Badge colorScheme={evidenceReview.advised_trades_without_evidence > 0 ? 'red' : 'green'}>
                                                    Need evidence
                                                </Badge>
                                            </VStack>
                                        )}

                                        {evidenceReview && (
                                            <VStack align="start" spacing={1}>
                                                <Text fontSize="xs" color={bodyTextColor}>Risk Mismatches</Text>
                                                <Text fontSize="2xl" fontWeight="bold" color={labelTextColor}>
                                                    {evidenceReview.risk_mismatch_cases}
                                                </Text>
                                                <Badge colorScheme={evidenceReview.risk_mismatch_cases > 0 ? 'red' : 'green'}>
                                                    Product vs profile
                                                </Badge>
                                            </VStack>
                                        )}
                                    </SimpleGrid>
                                </CardBody>
                            </Card>

                            {/* Charts Row */}
                            <SimpleGrid columns={{ base: 1, lg: 2 }} spacing={{ base: 4, md: 6 }}>
                                {/* Risk Distribution Chart */}
                                <Card
                                    bg={cardBg}
                                    borderRadius="lg"
                                    boxShadow="md"
                                    borderWidth="1px"
                                    borderColor={borderColor}
                                >
                                    <CardBody>
                                        <HStack spacing={2} mb={4}>
                                            <Icon as={FaChartBar} color={isDarkTheme ? accentColor : primaryColor} boxSize={6} />
                                            <Heading size={{ base: 'sm', md: 'md' }} color={headingTextColor}>
                                                Risk Distribution
                                            </Heading>
                                        </HStack>
                                        {riskDist.length > 0 ? (
                                            <ResponsiveContainer width="100%" height={280}>
                                                <BarChart data={riskDist} margin={{ top: 20, right: 30, left: 20, bottom: 20 }}>
                                                    <CartesianGrid strokeDasharray="3 3" stroke={chartGridColor} />
                                                    <XAxis dataKey="name" stroke={axisColor} tick={{ fill: axisColor }} />
                                                    <YAxis stroke={axisColor} tick={{ fill: axisColor }} />
                                                    <RechartsTooltip
                                                        contentStyle={{
                                                            backgroundColor: isDarkTheme ? '#2D3748' : cardBg,
                                                            border: `1px solid ${borderColor}`,
                                                            borderRadius: '8px',
                                                            color: isDarkTheme ? '#E2E8F0' : '#1A202C',
                                                        }}
                                                        formatter={(value) => [`${value} customers`, 'Count']}
                                                    />
                                                    <Bar dataKey="value" radius={[8, 8, 0, 0]}>
                                                        {riskDist.map((entry, index) => (
                                                            <Cell key={`cell-${index}`} fill={entry.color} />
                                                        ))}
                                                    </Bar>
                                                </BarChart>
                                            </ResponsiveContainer>
                                        ) : (
                                            <Flex justify="center" align="center" h="280px">
                                                <Text color={mutedTextColor}>No data available</Text>
                                            </Flex>
                                        )}
                                    </CardBody>
                                </Card>

                                {/* Bias Severity Distribution */}
                                <Card
                                    bg={cardBg}
                                    borderRadius="lg"
                                    boxShadow="md"
                                    borderWidth="1px"
                                    borderColor={borderColor}
                                >
                                    <CardBody>
                                        <HStack spacing={2} mb={4}>
                                            <Icon as={FaBrain} color={isDarkTheme ? accentColor : primaryColor} boxSize={6} />
                                            <Heading size={{ base: 'sm', md: 'md' }} color={headingTextColor}>
                                                Bias Severity Distribution
                                            </Heading>
                                        </HStack>
                                        {biasSeverity.length > 0 ? (
                                            <Box>
                                                <ResponsiveContainer width="100%" height={200}>
                                                    <PieChart>
                                                        <Pie
                                                            data={biasSeverity}
                                                            cx="50%"
                                                            cy="50%"
                                                            labelLine={false}
                                                            label={({ name, percent }) =>
                                                                `${name} ${(percent * 100).toFixed(0)}%`
                                                            }
                                                            outerRadius={70}
                                                            dataKey="value"
                                                        >
                                                            {biasSeverity.map((entry, index) => (
                                                                <Cell key={`cell-${index}`} fill={entry.color} />
                                                            ))}
                                                        </Pie>
                                                        <RechartsTooltip
                                                            contentStyle={{
                                                                backgroundColor: isDarkTheme ? '#2D3748' : cardBg,
                                                                border: `1px solid ${borderColor}`,
                                                                borderRadius: '8px',
                                                                color: isDarkTheme ? '#E2E8F0' : '#1A202C',
                                                            }}
                                                            formatter={(value) => [`${value} instances`, 'Count']}
                                                        />
                                                    </PieChart>
                                                </ResponsiveContainer>
                                                <SimpleGrid columns={2} spacing={2} mt={3}>
                                                    <Card bg={infoBg} borderRadius="md" boxShadow="sm">
                                                        <CardBody p={3}>
                                                            <Text fontSize="xs" color={bodyTextColor}>Total Detected</Text>
                                                            <Text fontSize="xl" fontWeight="bold" color={labelTextColor}>
                                                                {dashboardData.bias_severity_summary?.total_biases_detected?.toLocaleString()}
                                                            </Text>
                                                        </CardBody>
                                                    </Card>
                                                    <Card bg={infoBg} borderRadius="md" boxShadow="sm">
                                                        <CardBody p={3}>
                                                            <Text fontSize="xs" color={bodyTextColor}>Customers Affected</Text>
                                                            <Text fontSize="xl" fontWeight="bold" color={labelTextColor}>
                                                                {dashboardData.bias_severity_summary?.customers_with_biases?.toLocaleString()}
                                                            </Text>
                                                        </CardBody>
                                                    </Card>
                                                </SimpleGrid>
                                            </Box>
                                        ) : (
                                            <Flex justify="center" align="center" h="280px">
                                                <Text color={mutedTextColor}>No data available</Text>
                                            </Flex>
                                        )}
                                    </CardBody>
                                </Card>
                            </SimpleGrid>

                            {/* System Performance & Audit — telemetry blocks only exist in the
                                synthetic demo payload until real platform telemetry ships. */}
                            <SimpleGrid columns={{ base: 1, md: 2 }} spacing={{ base: 4, md: 6 }}>
                                {/* Data Privacy & Export Monitoring */}
                                {(metrics.export_volume || metrics.pii_exposure_check) && (
                                <Card
                                    bg={cardBg}
                                    borderRadius="lg"
                                    boxShadow="md"
                                    borderWidth="1px"
                                    borderColor={borderColor}
                                >
                                    <CardBody>
                                        <HStack spacing={2} mb={4}>
                                            <Icon as={FaFileExport} color={isDarkTheme ? accentColor : primaryColor} boxSize={6} />
                                            <Heading size={{ base: 'sm', md: 'md' }} color={headingTextColor}>
                                                Export Volume Monitoring
                                            </Heading>
                                        </HStack>
                                        <VStack spacing={4} align="stretch">
                                            <SimpleGrid columns={3} spacing={3}>
                                                <Card bg={successBg} borderRadius="md" boxShadow="sm">
                                                    <CardBody p={3} textAlign="center">
                                                        <Text fontSize="xs" color={bodyTextColor} mb={1}>CSV</Text>
                                                        <Text fontSize="2xl" fontWeight="bold" color={successColor}>
                                                            {metrics.export_volume?.csv}
                                                        </Text>
                                                    </CardBody>
                                                </Card>
                                                <Card bg={errorBg} borderRadius="md" boxShadow="sm">
                                                    <CardBody p={3} textAlign="center">
                                                        <Text fontSize="xs" color={bodyTextColor} mb={1}>PDF</Text>
                                                        <Text fontSize="2xl" fontWeight="bold" color={errorColor}>
                                                            {metrics.export_volume?.pdf}
                                                        </Text>
                                                    </CardBody>
                                                </Card>
                                                <Card bg={warningBg} borderRadius="md" boxShadow="sm">
                                                    <CardBody p={3} textAlign="center">
                                                        <Text fontSize="xs" color={bodyTextColor} mb={1}>PNG</Text>
                                                        <Text fontSize="2xl" fontWeight="bold" color={warningColor}>
                                                            {metrics.export_volume?.png}
                                                        </Text>
                                                    </CardBody>
                                                </Card>
                                            </SimpleGrid>
                                            <Divider borderColor={borderColor} />
                                            <HStack justify="space-between">
                                                <VStack align="start" spacing={0}>
                                                    <HStack>
                                                        <Icon as={FaLock} color={infoColor} boxSize={5} />
                                                        <Text fontSize="sm" fontWeight="bold" color={labelTextColor}>
                                                            PII Protection
                                                        </Text>
                                                    </HStack>
                                                    <Text fontSize="xs" color={bodyTextColor}>
                                                        Total Views: {metrics.pii_exposure_check?.total_views}
                                                    </Text>
                                                    <Text fontSize="xs" color={bodyTextColor}>
                                                        Masked: {metrics.pii_exposure_check?.total_masked_views}
                                                    </Text>
                                                </VStack>
                                                <VStack align="end" spacing={0}>
                                                    <Text fontSize="3xl" fontWeight="bold" color={successColor}>
                                                        {metrics.pii_exposure_check?.overall_masking_pct || 0}%
                                                    </Text>
                                                    <Text fontSize="xs" color={mutedTextColor}>Masking Rate</Text>
                                                </VStack>
                                            </HStack>
                                        </VStack>
                                    </CardBody>
                                </Card>
                                )}

                                {/* System Performance */}
                                {metrics.latency_slo_monitor && (
                                <Card
                                    bg={cardBg}
                                    borderRadius="lg"
                                    boxShadow="md"
                                    borderWidth="1px"
                                    borderColor={borderColor}
                                >
                                    <CardBody>
                                        <HStack spacing={2} mb={4}>
                                            <Icon as={FaTachometerAlt} color={isDarkTheme ? accentColor : primaryColor} boxSize={6} />
                                            <Heading size={{ base: 'sm', md: 'md' }} color={headingTextColor}>
                                                System Performance (SLO)
                                            </Heading>
                                        </HStack>
                                        <VStack spacing={4} align="stretch">
                                            <SimpleGrid columns={3} spacing={3}>
                                                <Card bg={infoBg} borderRadius="md" boxShadow="sm">
                                                    <CardBody p={3} textAlign="center">
                                                        <Text fontSize="xs" color={bodyTextColor} mb={1}>P50</Text>
                                                        <Text fontSize="xl" fontWeight="bold" color={infoColor}>
                                                            {metrics.latency_slo_monitor?.p50_ms}ms
                                                        </Text>
                                                    </CardBody>
                                                </Card>
                                                <Card bg={warningBg} borderRadius="md" boxShadow="sm">
                                                    <CardBody p={3} textAlign="center">
                                                        <Text fontSize="xs" color={bodyTextColor} mb={1}>P95</Text>
                                                        <Text fontSize="xl" fontWeight="bold" color={warningColor}>
                                                            {metrics.latency_slo_monitor?.p95_ms}ms
                                                        </Text>
                                                    </CardBody>
                                                </Card>
                                                <Card bg={errorBg} borderRadius="md" boxShadow="sm">
                                                    <CardBody p={3} textAlign="center">
                                                        <Text fontSize="xs" color={bodyTextColor} mb={1}>P99</Text>
                                                        <Text fontSize="xl" fontWeight="bold" color={errorColor}>
                                                            {metrics.latency_slo_monitor?.p99_ms}ms
                                                        </Text>
                                                    </CardBody>
                                                </Card>
                                            </SimpleGrid>
                                            <Divider borderColor={borderColor} />
                                            <HStack justify="space-between">
                                                <VStack align="start" spacing={0}>
                                                    <HStack>
                                                        <Icon
                                                            as={metrics.latency_slo_monitor?.slo_breached ? FaExclamationTriangle : FaCheckCircle}
                                                            color={metrics.latency_slo_monitor?.slo_breached ? errorColor : successColor}
                                                            boxSize={5}
                                                        />
                                                        <Text fontSize="sm" fontWeight="bold" color={labelTextColor}>
                                                            SLO Status
                                                        </Text>
                                                    </HStack>
                                                    <Text fontSize="xs" color={bodyTextColor}>
                                                        Sample Size: {metrics.latency_slo_monitor?.sample_size?.toLocaleString()}
                                                    </Text>
                                                </VStack>
                                                <Badge
                                                    colorScheme={metrics.latency_slo_monitor?.slo_breached ? 'red' : 'green'}
                                                    fontSize="md"
                                                    px={4}
                                                    py={2}
                                                >
                                                    {metrics.latency_slo_monitor?.slo_breached ? 'BREACHED' : 'HEALTHY'}
                                                </Badge>
                                            </HStack>
                                        </VStack>
                                    </CardBody>
                                </Card>
                                )}

                                {/* Evidence coverage — computed live from the suitability evidence dataset */}
                                {hasEvidenceCoverage && (
                                <Card
                                    bg={cardBg}
                                    borderRadius="lg"
                                    boxShadow="md"
                                    borderWidth="1px"
                                    borderColor={borderColor}
                                >
                                    <CardBody>
                                        <HStack spacing={2} mb={4}>
                                            <Icon as={FaFileAlt} color={purpleColor} boxSize={6} />
                                            <Heading size={{ base: 'sm', md: 'md' }} color={headingTextColor}>
                                                Audit Evidence Coverage
                                            </Heading>
                                        </HStack>
                                        <VStack spacing={3} align="stretch">
                                            <HStack justify="space-between">
                                                <Text fontSize="sm" color={bodyTextColor}>
                                                    Customers with linked suitability evidence
                                                </Text>
                                                <Text fontSize="2xl" fontWeight="bold" color={purpleColor}>
                                                    {Math.round(evidenceCoveragePct)}%
                                                </Text>
                                            </HStack>
                                            <Progress
                                                value={evidenceCoveragePct}
                                                h="8px"
                                                borderRadius="full"
                                                colorScheme={evidenceCoveragePct >= 80 ? 'green' : evidenceCoveragePct >= 50 ? 'yellow' : 'red'}
                                            />
                                            <Text fontSize="xs" color={mutedTextColor}>
                                                Computed from the uploaded suitability evidence dataset. Audit exports certify only what is loaded here.
                                            </Text>
                                        </VStack>
                                    </CardBody>
                                </Card>
                                )}
                            </SimpleGrid>
                        </>
                    )}

                    <ProductDataSignalsPanel
                        roleFilter="Compliance"
                        title="Live compliance signals (evidence, risk mismatch, transaction gaps)"
                        helperText="Signals are computed from suitability_evidence, customer_transactions, tenant_products and customer_holdings. Upload firm-ready datasets in Settings → Datasets to enrich this view."
                        emptyMessage="No suitability evidence, transaction-evidence, or product-risk mismatch signals for the Compliance queue."
                    />

                    {!loading && !dashboardData && (
                        <EmptyStateCard
                            icon={FaShieldAlt}
                            title="Compliance data is temporarily unavailable"
                            description="The compliance workspace could not load its latest monitoring snapshot."
                            hint="Try a refresh now. If the issue persists, confirm the current dataset completed processing and that the compliance content endpoint is healthy."
                            bg={cardBg}
                            borderColor={borderColor}
                            titleColor={headingTextColor}
                            descriptionColor={bodyTextColor}
                            hintColor={mutedTextColor}
                            iconColor={isDarkTheme ? accentColor : primaryColor}
                            maxW="4xl"
                        >
                            <Button
                                leftIcon={<FaSync />}
                                bg={primaryColor}
                                color="white"
                                size="sm"
                                onClick={fetchDashboardData}
                                isLoading={loading}
                                _hover={{ bg: buttonHoverBg }}
                            >
                                Retry dashboard load
                            </Button>
                        </EmptyStateCard>
                    )}
                </VStack>
            </Container>
        </Box>
    );
}

export default DashboardComplianceDashboard;
